// Radha — Collections writes, straight to PostgREST.
//
// The write half of the direct path (migration 027). Approve/reject a proposal
// and send a nudge, both guarded in the database: the proposal decision trigger
// stamps decided_by/decided_at and rejects illegal transitions; the nudge trigger
// enforces the RBI fair-collection rules and raises coded errors
// (NO_CONSENT / RBI_WINDOW / PROHIBITED_LANGUAGE / RBI_DAILY_CAP). The UI just
// surfaces whatever the database says.
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { supabase } from "../lib/supabase";
import { collectionsKeys } from "./useCollectionsDirect";
import { edgeFunctionErrorMessage } from "../lib/edgeFunctionError";

export type ProposalDecision = "approved" | "rejected";

export function useDecideProposal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, decision }: { id: string; decision: ProposalDecision }) => {
      const { data, error } = await supabase
        .from("collection_proposals")
        .update({ status: decision })
        .eq("id", id)
        .select("id, status, decided_at")
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: collectionsKeys.proposals });
    },
  });
}

export interface NudgeInput {
  account_id: string;
  channel: "email" | "sms" | "whatsapp" | "ivr_call";
  language: "en" | "hi" | "ta" | "te" | "mr" | "gu";
  body: string;
}

export function useSendNudge() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: NudgeInput) => {
      // tenant_id is set by the DB trigger from the caller's token; never sent.
      const { data, error } = await supabase
        .from("collection_nudges")
        .insert(input)
        .select("id, status, within_contact_window, local_hour_at_send, conduct_country")
        .single();
      // error.message carries the coded conduct reason (NO_CONSENT / REST_DAY /
      // CONTACT_WINDOW / PROHIBITED_LANGUAGE / DAILY_CAP / ROLLING_CAP — 064)
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: collectionsKeys.accounts });
    },
  });
}

// Tier-0 proposal generation (migration 029): deterministic, no AI. Creates a
// draft for each past-due account of this tenant that has none, and returns the
// count created.
export function useGenerateProposals() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<number> => {
      const { data, error } = await supabase.rpc("generate_collection_proposals");
      if (error) throw error;
      return (data as number) ?? 0;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: collectionsKeys.proposals });
    },
  });
}

// Tier-1 polish (Edge Function polish-proposal): rewrites the draft message via
// an LLM (Groq -> OpenAI -> Gemini fallback), re-checked RBI-clean, and flips the
// proposal to tier 1. Returns which provider answered.
export function usePolishProposal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (proposalId: string): Promise<{ provider: string }> => {
      const { data, error } = await supabase.functions.invoke("polish-proposal", {
        body: { proposal_id: proposalId },
      });
      if (error) throw new Error(await edgeFunctionErrorMessage(error));
      if (data?.error) throw new Error(data.error);
      return { provider: data?.provider ?? "ai" };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: collectionsKeys.proposals });
    },
  });
}

/** Turn a Supabase/PostgREST error into the short coded reason for the UI.
 *  PostgREST errors are plain objects (not Error instances), so read .message
 *  off any object that carries one — otherwise String() gives "[object Object]". */
export function writeErrorMessage(err: unknown): string {
  const msg =
    err instanceof Error ? err.message
      : err && typeof err === "object" && typeof (err as { message?: unknown }).message === "string"
        ? (err as { message: string }).message
        : String(err);
  // DB raises "CODE: human hint" — show the human hint, keep it readable.
  const m = msg.match(/^([A-Z_]+):\s*(.*)$/);
  return m ? m[2] || m[1] : msg;
}
